import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoardContext } from '@/contexts/BoardContext';
import { useAuth } from '@/hooks/useAuth';
import { useScreenSize } from '@/hooks/useScreenSize';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { DashboardStats } from '@/components/dashboard/DashboardStats';
import { BoardGrid } from '@/components/dashboard/BoardGrid';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { UpcomingDeadlines } from '@/components/dashboard/UpcomingDeadlines';
import { LoadingErrorBoundary } from '@/components/LoadingErrorBoundary';

const Dashboard = () => {
  const { user } = useAuth();
  const { boards, boardsLoading, boardsError, refetchBoards } = useBoardContext();
  const { isMobile, isTablet } = useScreenSize();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  if (boardsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <DashboardHeader 
        user={user}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      
      <LoadingErrorBoundary 
        isLoading={boardsLoading} 
        error={boardsError}
        onRetry={refetchBoards}
        loadingMessage="Loading your dashboard..."
        connectionState="connected"
      >
        <div className={`${isMobile ? 'p-4' : isTablet ? 'p-6' : 'p-8'} space-y-6`}>
          {/* Dashboard Stats */}
          <DashboardStats />

          {/* Quick Actions */}
          <QuickActions />

          {/* Main Content Grid */}
          <div className={`grid gap-6 ${
            isMobile 
              ? 'grid-cols-1' 
              : isTablet 
                ? 'grid-cols-1 lg:grid-cols-3' 
                : 'grid-cols-1 lg:grid-cols-3'
          }`}>
            {/* Boards Section - Takes up more space */}
            <div className={`${isMobile ? 'col-span-1' : 'lg:col-span-2'}`}>
              <BoardGrid viewMode={viewMode} />
            </div>

            {/* Sidebar Content */}
            <div className="space-y-6">
              <UpcomingDeadlines />
              <RecentActivity />
            </div>
          </div>
        </div>
      </LoadingErrorBoundary>
    </div>
  );
};

export default Dashboard;
